import { BottomCenteredDrawer } from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import { DataLoadingWrapper } from "@jugl-web/ui-components";
import { AgentRateLimiter, useRestApiProvider } from "@jugl-web/rest-api";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { DiscardDialog } from "@jugl-web/ui-kit/overlay/DiscardDialog";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MS_PER_MINUTE } from "@web-src/modules/cx/pages/CXAIAgentsPage/consts";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";
import {
  MessageLimitFormState,
  MessageLimitSection,
} from "./components/MessageLimitSection/MessageLimitSection";

export type AgentSettingsDrawerProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  entityId: string;
};

export const AgentSettingsDrawer: React.FC<AgentSettingsDrawerProps> = ({
  isOpen,
  onRequestClose,
  entityId,
}) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { integrationsApi } = useRestApiProvider();
  const { data, isLoading, isError, refetch } =
    integrationsApi.useGetAiAgentConfigQuery({ entityId }, { skip: !isOpen });
  const [updateAgentConfig, { isLoading: isSaving }] =
    integrationsApi.useUpdateAgentConfigMutation();
  const discardChangesConfirmationDialogState = useConfirmationDialogState();

  const mapRateLimiterToFormValues = useCallback(
    (rateLimiter?: AgentRateLimiter): MessageLimitFormState => {
      if (!rateLimiter) {
        return { limit: null, periodMinutes: null, msg: "" };
      }

      return {
        limit: rateLimiter.limit,
        periodMinutes: Math.max(
          1,
          Math.round(rateLimiter.period_ms / MS_PER_MINUTE)
        ),
        msg: rateLimiter.msg,
      };
    },
    []
  );

  const [messageLimitForm, setMessageLimitForm] =
    useState<MessageLimitFormState>({
      limit: null,
      periodMinutes: null,
      msg: "",
    });

  useEffect(() => {
    if (!isOpen || !data) return;
    setMessageLimitForm(mapRateLimiterToFormValues(data.config.rate_limiter));
  }, [data, isOpen, mapRateLimiterToFormValues]);

  const updateMessageLimitForm = useCallback(
    (patch: Partial<MessageLimitFormState>) => {
      setMessageLimitForm((current) => ({ ...current, ...patch }));
    },
    []
  );

  const savedMessageLimitForm = useMemo(
    () => (data ? mapRateLimiterToFormValues(data.config.rate_limiter) : null),
    [data, mapRateLimiterToFormValues]
  );

  const isDirty = useMemo(() => {
    if (!savedMessageLimitForm) return false;

    return (
      messageLimitForm.limit !== savedMessageLimitForm.limit ||
      messageLimitForm.periodMinutes !== savedMessageLimitForm.periodMinutes ||
      messageLimitForm.msg.trim() !== savedMessageLimitForm.msg.trim()
    );
  }, [messageLimitForm, savedMessageLimitForm]);

  const handleRequestClose = useCallback(() => {
    if (isSaving) return;

    if (isDirty) {
      discardChangesConfirmationDialogState.open({
        onConfirm: onRequestClose,
      });
      return;
    }

    onRequestClose();
  }, [
    discardChangesConfirmationDialogState,
    isDirty,
    isSaving,
    onRequestClose,
  ]);

  const saveMessageLimitSettings = useCallback(async () => {
    if (
      !data ||
      !isDirty ||
      messageLimitForm.limit == null ||
      messageLimitForm.periodMinutes == null
    ) {
      return;
    }

    try {
      await updateAgentConfig({
        entityId,
        config: {
          ...data.config,
          rate_limiter: {
            limit: messageLimitForm.limit,
            enabled: true,
            period_ms: messageLimitForm.periodMinutes * MS_PER_MINUTE,
            msg: messageLimitForm.msg.trim(),
          },
        },
      }).unwrap();

      toast(
        t({
          id: "cx-agent-setup.message-limit-was-updated",
          defaultMessage: "Message Limit was updated",
        })
      );
      onRequestClose();
    } catch {
      toast(
        t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong",
        }),
        { variant: "error" }
      );
    }
  }, [
    data,
    entityId,
    isDirty,
    messageLimitForm,
    onRequestClose,
    t,
    toast,
    updateAgentConfig,
  ]);

  return (
    <>
      <BottomCenteredDrawer
        isOpen={isOpen}
        onClose={handleRequestClose}
        size="fullscreen"
        header={{
          type: "title",
          title: (
            <div className="flex items-center gap-3">
              <SettingsIcon />
              <span>
                {t({
                  id: "cx-ai-agents-page.general-ai-settings",
                  defaultMessage: "General AI settings",
                })}
              </span>
            </div>
          ),
        }}
      >
        <BottomCenteredDrawer.Content className="flex items-start justify-center bg-grey-100 p-8">
          <DataLoadingWrapper
            isLoading={isLoading}
            isError={isError}
            onRetry={refetch}
          >
            <div className="flex h-auto w-full max-w-[1200px] shrink-0 justify-center">
              <MessageLimitSection
                value={messageLimitForm}
                onChange={updateMessageLimitForm}
                onSave={saveMessageLimitSettings}
                isSaving={isSaving}
                isDirty={isDirty}
              />
            </div>
          </DataLoadingWrapper>
        </BottomCenteredDrawer.Content>
      </BottomCenteredDrawer>
      <DiscardDialog
        isOpen={discardChangesConfirmationDialogState.isOpen}
        onCancel={discardChangesConfirmationDialogState.close}
        onDiscard={() =>
          discardChangesConfirmationDialogState.confirm({
            closeOnceConfirmed: true,
          })
        }
      />
    </>
  );
};
